import {
  Vue, Component, Watch, Inject,
} from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { Button } from '@hb/coffee';
import { ChannelApplyLimitEnum } from '@/api/apply-product/types';
import { AdjustTypeEnum } from '@/dict/quotaIncrease-type';
import type {
  ApplyProductData,
  ApplyChannelData,
} from '@/api/apply-product/types';
import ProductCommodity from '@/shared/commodity';
import { repayPlanModelType } from '@/po/ProductResult';
import type { CouponRolePo } from '@/api/coupon/types';
import { confirmDialog } from '@/components/dialog';
import { PreCreditUserSourceEnums } from '@/enums/home-type';
import type { FullAmountCouponType } from '@/views_v2/apply-product/types';
import type { UserSceneConfig } from '../../types';
import NewMoneyInputCom from '../new-money-input';
import LoadingSkeleton from '../loading-skeleton';

const ApplyProductStore = namespace('applyProduct');
const style = require('./index.module.scss') as Dictionary;

@Component
export default class MoneyInput extends Vue {
  @ApplyProductStore.Getter showFullAmountCouponTag!: boolean;

  @ApplyProductStore.State amount!: string;

  @ApplyProductStore.State maxAmount!: string;

  @ApplyProductStore.State fullAmountCoupon!: FullAmountCouponType;

  @ApplyProductStore.State channel!: ApplyChannelData;

  @ApplyProductStore.State totalAvailableAmount!: number;

  @ApplyProductStore.State product!: ApplyProductData;

  @ApplyProductStore.State coupon!: CouponRolePo;

  @ApplyProductStore.State sceneConfig!: UserSceneConfig;

  @ApplyProductStore.State multiChannelRepayment!: string

  @ApplyProductStore.Getter isDiscountModuleTempAmountExperiment!: boolean;

  @ApplyProductStore.Getter isCouponModuleDisabled!: boolean;

  @ApplyProductStore.Getter shouldShowTempAmountEntry!: boolean;

  @ApplyProductStore.Getter showTempAmountInDiscountModule!: boolean;

  @ApplyProductStore.Action $updateState!: (data: Dictionary) => void;

  @ApplyProductStore.Action applyTempAmountEntry!: () => Promise<void>;

  @ApplyProductStore.Action getMaxAmount!: () => Promise<void>;

  @ApplyProductStore.Action confirmFullAmount!: () => void;

  @ApplyProductStore.Action updateFullAmountCoupon!: (
    payload: Partial<FullAmountCouponType>) => void;

  @ApplyProductStore.Action getCouponList!: () => Promise<void>;

  @ApplyProductStore.Action getPeriodData!: (currentAmount: string) => Promise<void>;

  @ApplyProductStore.Action calcRepayPlans!: (payload?: {
    repayType: string;
    currentAmount: string;
  }) => Promise<void>;

  @Inject() readonly registValidator!: (
    func: (onFinish?: () => void) => string
  ) => void;

  // 申请金额验证错误信息
  private errMsg = '';

  // id
  private get channelId() {
    return (
      (this.product
        && this.product.productInfo
        && this.product.productInfo.productId)
      || '6800'
    );
  }

  // 获取金额输入框Placeholder
  private get moneyInputPlaceholder(): string {
    if (this.product && this.amount === '') {
      return `最大可提现额度${this.maxAmount}元`;
    }
    return '';
  }

  // 获取最大金额提示文案（无需做步数处理金额）
  private get moneyTips(): string {
    if (!this.channel) return '';
    const { availableAmount, preCredit, preCreditUserSource } = this.channel || {};

    if (preCredit && [PreCreditUserSourceEnums.NORMAL, PreCreditUserSourceEnums.SEA_CONSUMER]
      .includes(preCreditUserSource)) {
      return '最终可借额度以放款方审核为准';
    }

    if (Number(availableAmount) === this.totalAvailableAmount) {
      return `当前机构可提${Number(availableAmount)}元`;
    }

    return `最多可提${this.totalAvailableAmount}元，当前机构可提${Number(availableAmount)}元`;
  }

  // 当前所选机构额度调整提示
  private get channelQuota(): string {
    const { adjustQuota } = this.channel || {};
    const { adjustType, adjustValue = 0 } = adjustQuota || {};

    if (Number(adjustValue)) {
      if (adjustType === AdjustTypeEnum.INCREASE) {
        return `已提额${adjustValue}`;
      }
      if (
        adjustType === AdjustTypeEnum.TEMP
        && !this.isDiscountModuleTempAmountExperiment
        && !this.isCouponModuleDisabled
      ) {
        return `含临时额度${adjustValue}元`;
      }
      return '';
    }

    return '';
  }

  /** 是否在金额提示中展示提额入口 */
  private get shouldShowTempAmountEntryInMoneyTips(): boolean {
    // 优惠模块实验组 + 有临额：不在这里展示（在 temp-amount 模块展示）
    if (this.showTempAmountInDiscountModule) {
      return false;
    }
    return this.shouldShowTempAmountEntry;
  }

  /** 点击提额入口 */
  private handleApplyTempAmount(): void {
    this.applyTempAmountEntry();
  }

  private get availableAmount(): number {
    if (this.product && this.product.userCredit) {
      return this.product.userCredit.availableAmount;
    }
    return 0;
  }

  private get currentRepaymentLength() {
    return this.currentProductRepayMode.length;
  }

  private get currentProductRepayMode(): string[] {
    return (
      this.product && this.product.productInfo && this.product.productInfo.repaymentModel
    ) || [];
  }

  private get currentRepayType(): string | number {
    if (!this.multiChannelRepayment) {
      return this.currentRepaymentLength && this.currentProductRepayMode[0];
    }
    return this.multiChannelRepayment;
  }

  private checkAmount() {
    // 金额发生改变时候对数据有效性进行验证并刷新期限以及优惠券
    this.validate(false, (currentAmount) => {
      if (!this.errMsg) {
        this.getPeriodData(currentAmount);
        this.calcRepayPlans({
          repayType: repayPlanModelType[this.currentRepayType],
          currentAmount,
        });
        this.getCouponList();
      }
    });
  }

  /**
   * 切换渠道时，重新获取最大额度改变金额，再通过此处监听变化刷新优惠券列表
   * TODO: 当渠道剩余可用金额一致时，可能不会触发刷新优惠券列表，存在隐患
   * PS：输入框手动修改金额一致情况，数据类型不一样，会触发优惠券列表刷新
   */
  @Watch('amount')
  private onAmountChange() {
    // 对校验方法进行防抖设置，避免用户输入删除期间频繁调用接口
    this.checkAmount();
  }


  // 最大可提现金额变化时（切换通道时，最大可提现金额会发生改变），重新校验输入金额
  @Watch('maxAmount')
  private onMaxAmountChange() {
    this.validate(false);
  }

  private created() {
    this.registValidator(this.validate.bind(this, true));
  }

  private mounted() {
    if (this.amount) {
      this.validate(false);
    }
  }

  private renderTips() {
    const { preCredit, preCreditUserSource } = this.channel || {};

    if (preCredit && [PreCreditUserSourceEnums.NORMAL, PreCreditUserSourceEnums.SEA_CONSUMER]
      .includes(preCreditUserSource)) {
      return (
        <p class={style.slightTips}>{this.moneyTips}</p>
      );
    }
    if (this.moneyTips || this.channelQuota) {
      return (
        <div class={style.tipsRow}>
          <p class={style.tipsText}>
            <span class={style.preTips}>{this.moneyTips}</span>
            {this.channelQuota ? `(${this.channelQuota})` : ''}
          </p>
          {/* 提额入口（非优惠模块场景） */}
          {this.shouldShowTempAmountEntryInMoneyTips && (
            <Button
              class={style.tempAmountEntry}
              type="light"
              onclick={this.handleApplyTempAmount.bind(this)}
            >
              去提额
            </Button>
          )}
        </div>
      );
    }

    return <LoadingSkeleton class={style.loadingBlock} width={290} height={12} />;
  }

  private render() {
    // 如果是一次性提现全部要求，则不能编辑
    const inputReadonly = !this.channel
      || this.channel.applyLimit === ChannelApplyLimitEnum.ALL_LIMIT
      || this.sceneConfig.moneyAmountDisable;

    return (
      <NewMoneyInputCom
        channel={this.channel}
        amount={this.amount}
        maxAmount={this.maxAmount}
        fullAmountCoupon={this.fullAmountCoupon}
        showFullAmountCouponTag={this.showFullAmountCouponTag}
        editable={!inputReadonly}
        availableAmount={this.availableAmount}
        placeholder={this.moneyInputPlaceholder}
        onInput={this.onInputEvent.bind(this)}
        onChange={this.onChangeEvent.bind(this)}
        onClear={this.onClearClick.bind(this)}
        onClickAll={this.onAllAmountClick.bind(this)}
        onBlur={this.onBlurEvent.bind(this)}
        onSelectFullAmountCoupon={this.confirmFullAmount.bind(this)}
      >
        {!this.sceneConfig.moneyTipsHidden && (
          <div slot="tips" class={style.moneyTips}>
            {/* 提现金额、额度tips文案 */}
            {!this.errMsg
              ? this.renderTips()
              : <p class={style.errMsg}>{this.errMsg}</p>
            }
          </div>
        )}
      </NewMoneyInputCom>
    );
  }

  private onChangeEvent() {
    this.$ubtPageButton('臻有钱_提现申请_输入金额', '产品', this.channelId);
  }

  private onInputEvent(value: string) {
    this.$updateState({
      amount: value,
    });
  }

  /** 金额框失焦 */
  private onBlurEvent() {
    // 金额框失焦时，清空满额提现券默认选中状态
    this.updateFullAmountCoupon({
      needSelect: false,
    });
    if (this.product && this.product.productInfo) {
      const { minApplyAmount, productId } = this.product.productInfo;

      /** 自营渠道 & 提现金额小于最小提现金额 */
      if (productId === ProductCommodity.AUTARKY_B_COMMODITY
         && Number(this.amount) < Number(minApplyAmount)) {
        confirmDialog.open({
          title: `抱歉，单笔借款不能低于${minApplyAmount}元`,
          content: '',
          confirmText: '知道了',
          ubtName: '',
        }).finally(() => {
          /** 无论是确认还是关闭弹窗都矫正金额为最小提现金额 */
          this.$ubtPageButtonNew('finance_loan_apply_moneyDialogClose', {});
          this.$updateState({
            amount: String(minApplyAmount),
          });
        });
      }
    }
  }

  private onClearClick() {
    this.$updateState({
      amount: '',
    });

    if (this.$refs.input) {
      (this.$refs.input as HTMLInputElement).focus();
    }

    this.$ubtPageButton('臻有钱_提现申请_清空金额', '产品', this.channelId);
  }

  private onAllAmountClick() {
    this.getMaxAmount();

    this.$ubtPageButton(
      '臻有钱_提现申请_全部',
      '产品',
      this.product ? this.channelId : '',
    );
  }

  // 验证输入金额的合法性
  private validate(checkEmpty: boolean, onFinish?: (amount: string) => void): string {
    this.errMsg = '';

    if (this.amount === '') {
      this.errMsg = checkEmpty ? '请输入有效的提现金额' : '';
    } else if (!/^[1-9]\d*$/.test(this.amount)) {
      this.errMsg = '请输入有效的提现金额';
    } else if (this.product && this.product.productInfo) {
      const { minApplyAmount, applyAmountStep, checkAmountStep } = this.product.productInfo;
      const checkStep = checkAmountStep || applyAmountStep;

      if (Number(this.amount) < Number(minApplyAmount)) {
        this.errMsg = `单笔借款不能低于${minApplyAmount}元`;
      } else if (Number(this.amount) > Number(this.maxAmount)) {
        this.errMsg = `单笔最多可提现${this.maxAmount}元，可尝试多笔提现`;
      } else if (Number(this.amount) % Number(checkStep) !== 0
      && this.amount !== this.maxAmount) {
        // 提现不等于maxAmount时校验步长
        this.errMsg = `提现金额应该是${checkStep}的倍数`;
      }
    }

    // 验证成功之后执行指定的回调
    if (onFinish) {
      onFinish(this.amount || '');
    }

    return this.errMsg;
  }
}
